<template>
  <nav
    ref="navigation"
    class="flex-column"
    @keydown="moveFocusOnArrowKey"
  >
    <PrmInputText
      v-model="appSearch"
      autofocus="true"
      :placeholder="i18n.global.t('page.appOverview.search')"
    />

    <CheckboxShowAvailableApps />

    <PrmAccordion
      v-model:active-index="activeTabIndex"
      multiple
      unstyled
    >
      <PrmAccordionTab
        v-for="appGroup in appGroupList"
        :key="appGroup"
        unstyled
        :header="getAppGroupLabel(appGroup)"
        :pt="{
          headerAction: {
            class: 'flex-row align-center',
            style: {
              gap: '0.5rem',
            },
          },
          header: {
            style: {
              padding: '1rem',
              paddingLeft: 0,
            },
          },
          content: {
            class: 'flex-column',
          },
        }"
      >
        <template
          v-for="app in appMap[appGroup]"
          :key="app.appId"
        >
          <AppTile :app="app" />
        </template>
      </PrmAccordionTab>
    </PrmAccordion>
  </nav>
</template>

<script setup lang="ts">
import PrmAccordion from '@NitrexGroup/primevue/accordion';
import PrmAccordionTab from '@NitrexGroup/primevue/accordiontab';
import PrmInputText from '@NitrexGroup/primevue/inputtext';
import {
  i18n,
} from '@i18n';
import {
  computed, ref, shallowRef, useTemplateRef, watch,
} from 'vue';

import AppTile from '@/components/app-tile/app-tile.vue';
import CheckboxShowAvailableApps from '@/components/checkbox-show-available-apps/checkbox-show-available-apps.vue';
import {
  useArrowKeyFocus,
} from '@/composition/use-arrow-key-focus.ts';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  TAppGroup, TAppMap,
} from '@/types/app-type.ts';
import {
  getAppGroupLabel,
  getAppMap,
} from '@/types/app-util.ts';
import {
  appTileColorMap,
} from '@/utils/app-tile.ts';

const props = defineProps<{
  isSidebarVisible: boolean,
  closeSidebar: () => void,
}>();

const appStore = useAppStore();
appStore.loadAppList();

const {
  moveFocusOnArrowKey,
} = useArrowKeyFocus({
  containerElement: useTemplateRef<HTMLElement>('navigation'),
  focusableSelector: '.p-inputtext, .app-tile__link',
});

const activeTabIndex = shallowRef<number[]>(Object.keys(appTileColorMap).map((_, index) => index));

const appSearch = ref('');

const appMap = computed<TAppMap>(() => {
  if (!appStore.appList) {
    return {} as TAppMap;
  }

  return getAppMap(appStore.getAvailableApps, appSearch.value);
});

const appGroupList = computed<TAppGroup[]>(() => {
  const result: TAppGroup[] = [];

  Object.keys(appMap.value).forEach((group) => {
    if (appMap.value[group as TAppGroup].length) {
      result.push(group as TAppGroup);
    }
  });

  return result;
});

watch(
  () => props.isSidebarVisible,
  () => {
    appSearch.value = '';
  },
);
watch(
  () => appStore.isNavigating,
  () => {
    appSearch.value = '';
    props.closeSidebar();
  },
);
</script>
